/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiBadge from "components/VuiBadge";

import { useLocation, NavLink } from 'react-router-dom';

import { useBadges, useReloadNow } from "context/app";

// Custom styles for the SidenavLink
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
  collapseArrow,
} from "examples/Sidenav/styles/sidenavCollapse";

// Vision UI Dashboard PRO React context
import { useVisionUIController } from "context";

// Models
import type { SidenavColor } from "models/theme.model";

interface SidenavLinkProps {
  color?: SidenavColor;
  // An icon name, or the icon itself.
  icon: ReactNode;
  name: string;
  children?: ReactNode;
  active?: boolean;
  noCollapse?: boolean;
  open?: boolean;
  route: string;
  // The badges key whose count shows next to the name.
  badgeName?: string;
}

function SidenavLink({ color = "info", icon, name, children, active = false, noCollapse = false, open = false, route, badgeName, ...rest }: SidenavLinkProps) {
  const [controller] = useVisionUIController();
  const { miniSidenav, transparentSidenav } = controller;

  const [badgeData, setBadgeData] = useState<number | null>(null);

  const { badges } = useBadges();
  const reloadNow = useReloadNow();

  useEffect(() => {
    if (badges && badgeName && badges[badgeName]) {
      setBadgeData(badges[badgeName])
    }
    
  }, [badges]);

  return (
    <NavLink className="menu-main-link" to={route} onClick={() => reloadNow()}>
      <ListItem component="li">
        <VuiBox {...rest} sx={(theme) => collapseItem(theme, { active, transparentSidenav })}>
          <ListItemIcon
            sx={(theme) => collapseIconBox(theme, { active, transparentSidenav, color })}
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
            ) : (
              icon
            )}
          </ListItemIcon>
          <ListItemText
            primary={name}
            sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav, active })}
          />
          {badgeData && (
            <VuiBadge badgeContent={badgeData} container color="primary" />
          )}
        </VuiBox>
      </ListItem>
    </NavLink>
  );
}

export default SidenavLink;
